import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, StatusBadge, MarginBadge, Folio, Empty } from "../components/common";
import { useFetch } from "../lib/api";
import { useT } from "../lib/i18n";
import { money, fdate } from "../lib/format";

const Kpi = ({ label, value, accent, testid }) => (
  <div className="min-w-0 rounded-md border border-neutral-200 bg-white p-4 sm:p-5">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
    <div data-testid={testid} className={`mt-2 break-words font-mono text-lg font-bold sm:text-xl 2xl:text-2xl ${accent ? "text-[#db0720]" : "text-black"}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const { t } = useT();
  const { data } = useFetch("/dashboard");
  if (!data) return <Empty>{t("common.loading")}</Empty>;
  const showCost = data.avg_margin_pct !== undefined;
  return (
    <div>
      <PageHeader title={t("dash.title")} kicker="BOBST LATAM" />
      <div className="stagger grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 md:grid-cols-3 2xl:grid-cols-6">
        <Kpi label={t("dash.quoted")} value={money(data.quoted_net_usd)} testid="kpi-quoted" />
        {showCost && <Kpi label={t("dash.gp")} value={money(data.gross_profit_usd)} testid="kpi-gp" />}
        {showCost && <Kpi label={t("dash.margin")} value={`${data.avg_margin_pct}%`} accent testid="kpi-margin" />}
        <Kpi label={t("dash.published")} value={`${data.published} / ${data.total_quotes}`} testid="kpi-published" />
        <Kpi label={t("dash.pending")} value={data.pending_changes} testid="kpi-pending" />
        <Kpi label={t("dash.nontax")} value={data.non_definitive_tax} accent={data.non_definitive_tax > 0} testid="kpi-nontax" />
      </div>
      <div className="mt-6 grid gap-6 2xl:grid-cols-[1.2fr_1fr] [&>*]:min-w-0">
        <div className="rounded-md border border-neutral-200 bg-white p-5" data-testid="chart-by-country">
          <div className="mb-4 text-sm font-bold uppercase tracking-wider">{t("dash.bycountry")}</div>
          {data.by_country.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.by_country}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ececec" vertical={false} />
                <XAxis dataKey="country" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="net_usd" fill="#db0720" radius={[3, 3, 0, 0]} name={t("ws.net")} />
                {showCost && <Bar dataKey="gp_usd" fill="#505050" radius={[3, 3, 0, 0]} name={t("ws.gp")} />}
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty>{t("common.empty")}</Empty>}
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(data.by_status).map(([s, n]) => (
              <span key={s} className="flex items-center gap-2 text-xs"><StatusBadge status={s} /> <b>{n}</b></span>
            ))}
          </div>
        </div>
        <div className="rounded-md border border-neutral-200 bg-white" data-testid="recent-quotes">
          <div className="border-b border-neutral-200 px-5 py-3 text-sm font-bold uppercase tracking-wider">{t("dash.recent")}</div>
          <div className="overflow-x-auto"><table className="dense-table w-full">
            <tbody>
              {data.recent.map((q) => (
                <tr key={q.id}>
                  <td><Link to={`/quotations/${q.id}`} className="hover:underline"><Folio>{q.folio}</Folio></Link></td>
                  <td className="max-w-[180px] truncate">{q.customer_name}</td>
                  <td><StatusBadge status={q.status} /></td>
                  <td className="num">{money(q.totals?.total, q.currency)}</td>
                  {showCost && <td><MarginBadge value={q.totals?.margin_pct} /></td>}
                  <td className="text-neutral-500">{fdate(q.quote_date)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
